import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { QuickLink } from "models/propTypes/commonPropTypes";
//#endregion
//#region components
import { ActionLinksGroup } from "components/common";
import { useLocation, useNavigate } from "react-router-dom";
import { useLastReconciliation } from "components/accounting";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const RevisorDocuments: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const annualAccountsFinancialYear = useSelector(
    (store: AppState) => store.auth.selectedCompany.annualAccountsFinancialYear
  );

  const loggedInUserId = useSelector((store: AppState) => store.auth.loggedInUser.personId);
  const loggedInUserName = useSelector((store: AppState) => store.auth.loggedInUser.name);

  const { getLastReconciliation, lastReconciliation } = useLastReconciliation();

  React.useEffect(() => {
    getLastReconciliation();
  }, []);

  const annualAccountsFinancialYearEnd = annualAccountsFinancialYear?.end
    ? DateHelper.convertDateToShortISOString(new Date(annualAccountsFinancialYear?.end))
    : undefined;

  const onSieFilesClick = () => {
    const { UploadedById, UploadedByName } = FormFieldConstants.Document.SIEFilter;

    const params = new URLSearchParams({
      [UploadedById]: loggedInUserId.toString(),
      [UploadedByName]: loggedInUserName,
    });

    navigate(
      {
        pathname: UrlHelper.formatPath("/Reports/SIE"),
        search: `?${params.toString()}`,
      },
      { state: { background: location } }
    );
  };

  const onSharedDocumentsClick = () => {
    navigate(UrlHelper.formatPath("/Reports/SharedDocuments"), {
      state: { background: location },
    });
  };

  const onAnnualAccountsClick = () => {
    navigate(UrlHelper.formatPath("/Reports/FinancialStatements"), {
      state: { background: location },
    });
  };

  const onReconciliationReportClick = () => {
    if (!lastReconciliation) return;

    const { ReconciliationId, Reconciliation } = FormFieldConstants.Accounting.ReconciliationReportFilter;
    const { reconciliationId, startMonth, endMonth } = lastReconciliation;

    const params = new URLSearchParams({
      [ReconciliationId]: reconciliationId.toString(),
      [Reconciliation]: DateHelper.formatAPIYearMonthRange(startMonth, endMonth),
    });

    navigate(
      {
        pathname: UrlHelper.formatPath("/Reports/ReconciliationReport"),
        search: `?${params.toString()}`,
      },
      { state: { background: location } }
    );
  };

  const links: QuickLink[] = [
    {
      icon: IconDefinitionConstants.WCompany,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.SIEFiles),
      onClickHandler: onSieFilesClick,
      hidden: !annualAccountsFinancialYearEnd,
    },
    {
      icon: IconDefinitionConstants.WMoneyBag,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.AnnualAccounts),
      onClickHandler: onAnnualAccountsClick,
      hidden: !annualAccountsFinancialYear?.isAutoAnnualReport,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.YearEndDocuments),
      onClickHandler: onSharedDocumentsClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.FinancialStatementSpecifications),
      onClickHandler: onReconciliationReportClick,
      hidden: !lastReconciliation?.reconciliationId,
    },
  ];

  return <ActionLinksGroup links={links} columns={{ xs: 12, sm: 6, md: 4, lg: 6, xl: 6 }} />;
};

export default RevisorDocuments;
